// src/utils/availabilitySlots.js
//
// Pure logic behind the calendar's Availability tab (no React, no Firestore),
// shared by pages/Calendar.js and calendar/EmbeddedCalendar.js.
//
// Rules (agreed for pest control + construction):
//   - The day is split into 2-hour blocks, 8AM-6PM. Each block label is the
//     same string as the Scheduling Start Time preset, so clicking an open
//     block can prefill the form directly.
//   - A job WITH a time frame ("10AM - 12PM", or custom "09:30" + endTime)
//     books every block it overlaps, for each assigned tech.
//   - A job with "ANY" / no time is an all-day job: that tech's whole day
//     is booked.
//   - A one-time job spanning several days (startDate != endDate) is all-day
//     on every day it runs, whatever its time frame.
//   - Approved time off / absences block the tech (partial days block only
//     the hours they cover).
//   - Open blocks in the past are shown as "past" and cannot be scheduled.

export const AVAILABILITY_SLOTS = [
  { key: "8AM - 10AM", start: 8, end: 10, label: "8–10 AM" },
  { key: "10AM - 12PM", start: 10, end: 12, label: "10 AM–12 PM" },
  { key: "12PM - 2PM", start: 12, end: 14, label: "12–2 PM" },
  { key: "2PM - 4PM", start: 14, end: 16, label: "2–4 PM" },
  { key: "4PM - 6PM", start: 16, end: 18, label: "4–6 PM" },
];

const DEFAULT_CUSTOM_DURATION_HOURS = 2;
const HIDDEN_STATUSES = new Set(["deleted", "cancelled", "canceled"]);

export const normalizeName = (value = "") =>
  String(value || "").toLowerCase().replace(/\s+/g, " ").trim();

const to24 = (hour, minutes, suffix) => {
  let h = Number(hour);
  const s = String(suffix || "").toUpperCase();
  if (s === "PM" && h !== 12) h += 12;
  if (s === "AM" && h === 12) h = 0;
  return h + (Number(minutes) || 0) / 60;
};

const parseClock24 = (value) => {
  const m = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h + min / 60;
};

const parseClock12 = (value) => {
  const m = String(value || "").trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  return m ? to24(m[1], m[2], m[3]) : null;
};

// { start, end } in decimal hours, or null when the job has no time frame
// (ANY / blank / unreadable) and should be treated as all-day.
export const parseJobTimeRange = (job = {}) => {
  const raw = String(job.startTime || "").trim();
  if (!raw || raw.toUpperCase() === "ANY") return null;

  // Preset window: "10AM - 12PM", "9:30AM - 11AM"
  const windowMatch = raw.match(
    /^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*[-–]\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i
  );
  if (windowMatch) {
    const start = to24(windowMatch[1], windowMatch[2], windowMatch[3]);
    const end = to24(windowMatch[4], windowMatch[5], windowMatch[6]);
    return end > start ? { start, end } : null;
  }

  // Custom start: "09:30" (24h) or "9:30 AM"
  const start = parseClock24(raw) ?? parseClock12(raw);
  if (start == null) return null;

  const endRaw = String(job.endTime || "").trim();
  const end = parseClock24(endRaw) ?? parseClock12(endRaw);
  return {
    start,
    end: end != null && end > start ? end : start + DEFAULT_CUSTOM_DURATION_HOURS,
  };
};

const formatHour = (decimal) => {
  const h = Math.floor(decimal);
  const min = Math.round((decimal - h) * 60);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return min ? `${h12}:${String(min).padStart(2, "0")} ${suffix}` : `${h12} ${suffix}`;
};

export const formatRange = (range) =>
  range ? `${formatHour(range.start)} – ${formatHour(range.end)}` : "All Day";

const dateKeyOf = (value) => {
  if (!value) return "";
  const d = typeof value?.toDate === "function" ? value.toDate() : value;
  if (typeof d === "string") return d.slice(0, 10);
  if (d instanceof Date && !Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return "";
};

const isRecurring = (job = {}) => {
  const type =
    job?.recurrence && typeof job.recurrence === "object"
      ? job.recurrence.type
      : job?.recurrence;
  return String(type || "none").toLowerCase().trim() !== "none";
};

// A one-time job that spans more than one calendar day.
export const isMultiDayJob = (job = {}) => {
  if (isRecurring(job)) return false;
  const start = dateKeyOf(job.startDate);
  const end = dateKeyOf(job.endDate || job.startDate);
  return Boolean(start && end && end > start);
};

export const getJobTechNames = (job = {}) => {
  const raw = job.techAssign;
  const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return list.map((n) => String(n || "").trim()).filter(Boolean);
};

const dayIndexInRange = (job, dateKey) => {
  const start = dateKeyOf(job.startDate);
  const end = dateKeyOf(job.endDate || job.startDate);
  if (!start || !end) return null;
  const ms = (k) => new Date(`${k}T12:00:00`).getTime();
  const total = Math.round((ms(end) - ms(start)) / 86400000) + 1;
  const index = Math.round((ms(dateKey) - ms(start)) / 86400000) + 1;
  return { index, total };
};

// "Site - Scope - Time" text for a booked tile.
export const describeJobForTile = (job = {}, dateKey = "") => {
  const site = job.siteName || job.propertyName || "Job";
  const scope = job.scope || job.serviceType || "";
  let time;
  if (isMultiDayJob(job)) {
    const pos = dateKey ? dayIndexInRange(job, dateKey) : null;
    time = pos ? `All Day (day ${pos.index} of ${pos.total})` : "All Day";
  } else {
    const range = parseJobTimeRange(job);
    time = range ? (job.startTime && /[AP]M/i.test(job.startTime) ? job.startTime : formatRange(range)) : "All Day";
  }
  return [site, scope, time].filter(Boolean).join(" - ");
};

const overlaps = (range, slot) => range.start < slot.end && range.end > slot.start;

// Hours a time-off entry blocks on its day: "all" or { from, to }.
const timeOffBlock = (entry = {}) => {
  const type = entry.dayType;
  const t = parseClock24(entry.partialTime);
  if (type === "leaveEarly" && t != null) return { from: t, to: 24 };
  if (type === "arriveLate" && t != null) return { from: 0, to: t };
  return "all";
};

/**
 * Build the availability grid for one day.
 *
 * @param jobs        rendered jobs for this date (already expanded for
 *                    recurrence by the calendar)
 * @param techs       ordered tech display names ("First Last") to show
 * @param timeOff     time-off entries for this date (entry.name = person)
 * @param dateKey     "YYYY-MM-DD"
 * @param now         Date used to grey out past blocks
 * @returns {
 *   columns: [{ name, allDayJobs, otherTimeJobs, timeOff, slots: [
 *       { ...slot, status: "booked"|"open"|"off"|"past"|"allday", jobs } ] }],
 *   unassigned: { allDayJobs, timedJobs },
 *   openCount
 * }
 */
export const buildDayAvailability = ({ jobs = [], techs = [], timeOff = [], dateKey, now = new Date() }) => {
  const todayKey = dateKeyOf(now);
  const nowHour = now.getHours() + now.getMinutes() / 60;
  const isPastDay = dateKey < todayKey;
  const isToday = dateKey === todayKey;

  const visibleJobs = jobs.filter(
    (j) => !HIDDEN_STATUSES.has(String(j?.status || "").toLowerCase().trim())
  );

  const unassigned = { allDayJobs: [], timedJobs: [] };
  const byTech = new Map(techs.map((name) => [normalizeName(name), { allDay: [], timed: [] }]));

  visibleJobs.forEach((job) => {
    const range = isMultiDayJob(job) ? null : parseJobTimeRange(job);
    const names = getJobTechNames(job);
    if (!names.length) {
      (range ? unassigned.timedJobs : unassigned.allDayJobs).push(job);
      return;
    }
    names.forEach((name) => {
      const bucket = byTech.get(normalizeName(name));
      if (!bucket) return;
      if (range) bucket.timed.push({ job, range });
      else bucket.allDay.push(job);
    });
  });

  let openCount = 0;

  const columns = techs.map((name) => {
    const bucket = byTech.get(normalizeName(name)) || { allDay: [], timed: [] };
    const offEntries = timeOff.filter((e) => normalizeName(e?.name) === normalizeName(name));
    const offBlocks = offEntries.map(timeOffBlock);
    const fullyOff = offBlocks.includes("all");
    const hasAllDay = bucket.allDay.length > 0;

    const slots = AVAILABILITY_SLOTS.map((slot) => {
      const slotJobs = bucket.timed.filter((t) => overlaps(t.range, slot)).map((t) => t.job);
      let status;
      if (slotJobs.length) status = "booked";
      else if (hasAllDay) status = "allday";
      else if (
        fullyOff ||
        offBlocks.some((b) => b !== "all" && b.from < slot.end && b.to > slot.start)
      ) status = "off";
      else if (isPastDay || (isToday && slot.end <= nowHour)) status = "past";
      else status = "open";

      if (status === "open") openCount += 1;
      return { ...slot, status, jobs: slotJobs };
    });

    const otherTimeJobs = bucket.timed
      .filter((t) => !AVAILABILITY_SLOTS.some((slot) => overlaps(t.range, slot)))
      .map((t) => t.job);

    return {
      name,
      allDayJobs: bucket.allDay,
      otherTimeJobs,
      timeOff: offEntries,
      fullyOff,
      canScheduleAllDay:
        !hasAllDay && !fullyOff && !isPastDay && bucket.timed.length === 0 && offEntries.length === 0,
      slots,
    };
  });

  return { columns, unassigned, openCount };
};
